import { PAGE_LIMIT_MAX, errorWords } from "@alm/domain";
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { saveFile } from "@/lib/files/download";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Download, MoreHorizontal } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { formToInput, ResidentForm, residentToForm } from "../-components/resident-form";
import { useLockedResidentFields, withoutLocked } from "../-components/resident-locked-fields";
import { SendDocumentsMenu } from "@/components/app/sending/send-documents-menu";
import { ChartActionsContext } from "./-components/chart/chart-actions";
import { ChartHeader } from "./-components/chart/chart-header";
import { ChartScope } from "./-components/chart/chart-scope";
import { useHiddenChartTabs } from "./-components/chart/chart-tab-access";
import { tabForPath, tabByValue } from "./-components/chart/chart-tab-list";
import { ChartTabs } from "./-components/chart/chart-tabs";
import { ReadmitButton } from "./-components/stay/readmit-button";
import { ResidentMergeDialog } from "../-components/resident-merge-dialog";

/** Chart layout: header, actions and the tab strip; each tab is a child route. */
export const Route = createFileRoute("/_authenticated/_care/residents/$residentId")({
  /* `?tab=family` addresses a tab: mention links are built that way, and
     older ones sit in emails, bookmarks and notifications, so they land on the
     same tab here. */
  beforeLoad: ({ location, params }) => {
    const wanted = new URLSearchParams(location.searchStr).get("tab");
    const tab = wanted ? tabByValue(wanted) : undefined;
    if (tab && tab.value !== "profile") throw redirect({ to: tab.to, params });
  },
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      orpc.care.residents.get.queryOptions({ input: { residentId: params.residentId } }),
    ),
  component: ResidentChartLayout,
});

function ResidentChartLayout() {
  const { residentId } = Route.useParams();
  const { data: resident } = useSuspenseQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId } }),
  );
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const active = tabForPath(pathname);
  /* Workspaces drop the chart's header, 260px rail and tab strip, which would cost a quarter of the
   * width; each links "Back to the chart". */
  const workspace =
    /\/(admission-packets|therapy-notes|therapy-referrals|plans-of-care|al-orders)\//.test(
      pathname,
    );
  const facilities = useQuery(
    orpc.administration.facilities.list.queryOptions({ input: { includeInactive: true } }),
  );
  const canUpdate = useCan("resident:update", resident.facilityId);
  const locked = useLockedResidentFields(resident);
  const canDelete = useCan("resident:delete", resident.facilityId);
  const canMerge = useCan("resident:merge", resident.facilityId);
  // Readmitting is admitting, at the resident's building: the permission the CRM's door asks for.
  const canAdmit = useCan("resident:create", resident.facilityId);
  const hiddenTabs = useHiddenChartTabs(resident.facilityId);
  const [editing, setEditing] = useState(false);
  const [merging, setMerging] = useState(false);
  const chartActions = useMemo(
    () => ({ editResident: canUpdate ? () => setEditing(true) : undefined }),
    [canUpdate],
  );
  /* Only fetched once somebody opens the dialog: a chart should not pay for a
     roster of two hundred people to render a button nobody pressed. */
  const mergeable = useQuery({
    ...orpc.care.residents.list.queryOptions({
      input: {
        ...(resident.facilityId ? { facilityId: resident.facilityId } : {}),
        limit: PAGE_LIMIT_MAX,
        offset: 0,
        sort: "name" as const,
      },
    }),
    enabled: merging,
  });
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const update = useMutation(
    orpc.care.residents.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Resident updated");
        setEditing(false);
        await queryClient.invalidateQueries({ queryKey: orpc.care.residents.key() });
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const faceSheet = useMutation(
    orpc.care.residents.faceSheet.mutationOptions({
      onSuccess: saveFile,
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const remove = useMutation(
    orpc.care.residents.remove.mutationOptions({
      onSuccess: async () => {
        toast.success("Resident removed");
        await queryClient.invalidateQueries({ queryKey: orpc.care.residents.key() });
        await navigate({ to: "/residents" });
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  if (workspace) {
    return (
      <ChartScope facilityId={resident.facilityId}>
        <ChartActionsContext.Provider value={chartActions}>
          <Outlet />
        </ChartActionsContext.Provider>
      </ChartScope>
    );
  }

  return (
    <ChartScope facilityId={resident.facilityId}>
      <ChartHeader
        resident={resident}
        tabs={<ChartTabs residentId={residentId} active={active} hidden={hiddenTabs} />}
        actions={
          <>
            {/* Readmit only for somebody who has left: offering it to a
                resident who is here is an invitation to a mistake. */}
            {/* A chart billing for a lead moves in through the lead, which carries its records. */}
            {canAdmit && resident.pendingLeadId ? (
              <Button asChild>
                <Link
                  to="/crm/leads/$leadId"
                  params={{ leadId: resident.pendingLeadId }}
                  search={{ convert: "move-in" }}
                >
                  Move in now
                </Link>
              </Button>
            ) : canAdmit && resident.status === "discharged" ? (
              <ReadmitButton residentId={residentId} room={resident.roomNumber} />
            ) : null}
            {/* The Face sheet tab carries its own Print and Download PDF. */}
            {active === "facesheet" ? null : (
              <Button
                variant="outline"
                aria-label="Download face sheet"
                disabled={faceSheet.isPending}
                onClick={() => faceSheet.mutate({ residentId })}
              >
                <Download className="size-4" />
                {faceSheet.isPending ? "Preparing…" : "Face sheet"}
              </Button>
            )}
            {canUpdate ? (
              <Button variant="outline" onClick={() => setEditing(true)}>
                {/* "Edit" on a phone so the four actions keep one row; the
                    name still says whom. */}
                <span>
                  Edit<span className="@max-2xl/record:sr-only"> resident</span>
                </span>
              </Button>
            ) : null}
            <SendDocumentsMenu
              residentId={residentId}
              facilityId={resident.facilityId ?? undefined}
            />
            {/* Merging is a thing you do TO a resident, so it belongs on
                their chart as well as on the roster. Rare, so it waits in the
                overflow with Delete. */}
            {canMerge || canDelete ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label={`More actions for ${resident.firstName} ${resident.lastName}`}
                  >
                    <MoreHorizontal className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canMerge ? (
                    <DropdownMenuItem onSelect={() => setMerging(true)}>
                      Merge with a duplicate…
                    </DropdownMenuItem>
                  ) : null}
                  {canMerge && canDelete ? <DropdownMenuSeparator /> : null}
                  {canDelete ? (
                    <DropdownMenuItem
                      variant="destructive"
                      onSelect={() => setConfirmingDelete(true)}
                    >
                      Delete resident…
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </>
        }
      />
      <ResidentMergeDialog
        open={merging}
        onOpenChange={setMerging}
        residents={mergeable.data?.rows ?? []}
        // This resident is the one kept; the duplicate is the one you pick.
        preset={["", residentId]}
      />
      <div className="mt-4 min-w-0">
        <ChartActionsContext.Provider value={chartActions}>
          <Outlet />
        </ChartActionsContext.Provider>
      </div>
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit resident</DialogTitle>
          </DialogHeader>
          <ResidentForm
            facilities={facilities.data ?? []}
            defaultValues={residentToForm(resident)}
            pending={update.isPending}
            locked={locked}
            onCancel={() => setEditing(false)}
            onSubmit={(values) =>
              update.mutate({ residentId, ...withoutLocked(formToInput(values), locked) })
            }
          />
        </DialogContent>
      </Dialog>
      <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {resident.firstName} {resident.lastName}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This removes the chart and everything attached to it. It cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep resident</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={() => remove.mutate({ residentId })}>
              Delete resident
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ChartScope>
  );
}
